import { useState, useEffect, useMemo, useRef } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/features/auth/AuthContext"
import { useEffectiveAdminAccess } from "@/features/admin/useEffectiveAdminAccess"
import { useBodyScrollLock } from "@/shared/lib/hooks/useBodyScrollLock"
import { isDepositsUiEnabled } from "@/features/deposits/depositsUiEnabled"
import { NotificationBell } from "@/widgets/notifications/NotificationBell"
import { publicAssetUrl } from "@/lib/publicAssetUrl"
import {
  PORTAL_ADMIN,
  PORTAL_DEPOSIT,
  PORTAL_EVENTS,
  PUBLIC_DEPOSITS,
} from "@/app/router/portalRoutes"
import { isNavItemActive } from "./navActiveMatchers"
import { NavAnimatedLink } from "./NavAnimatedLink"
import { NavbarUserMenuItems } from "./NavbarUserMenuItems"
import { NavbarPartners } from "./NavbarPartners"

type NavLink = {
  key: string
  label: string
  to: string
  activeKey: Parameters<typeof isNavItemActive>[0]
}

const getEntranceClass = (shouldAnimate: boolean, delayClass: string) =>
  shouldAnimate ? `hero-fade-up ${delayClass}` : ""

export default function Navbar() {
  const { t } = useTranslation()
  const { isAuthenticated, logout } = useAuth()
  const { hasAnyAdminAccess, adminAccessResolved } = useEffectiveAdminAccess()
  const [scrolled, setScrolled] = useState(false)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const location = useLocation()
  const menuButtonRef = useRef<HTMLButtonElement | null>(null)
  const entranceDoneRef = useRef(false)

  const isHome = location.pathname === "/"
  const isAtTop = !scrolled
  const isHomeHero = isHome && isAtTop
  const headerTone = isHomeHero ? "transparent" : "solid"
  const shouldAnimateEntrance = isHome && !entranceDoneRef.current

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60)
    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  useEffect(() => {
    if (!isHome || entranceDoneRef.current) return
    const timer = window.setTimeout(() => {
      entranceDoneRef.current = true
    }, 750)
    return () => window.clearTimeout(timer)
  }, [isHome])

  useEffect(() => {
    setMobileOpen(false)
    setUserMenuOpen(false)
  }, [location.pathname])

  useBodyScrollLock(mobileOpen)

  useEffect(() => {
    if (!mobileOpen) return
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault()
        setMobileOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener("keydown", handleEscape)
    return () => document.removeEventListener("keydown", handleEscape)
  }, [mobileOpen])

  const navLinks = useMemo<NavLink[]>(() => {
    const links: NavLink[] = []
    links.push({
      key: "events",
      label: t("nav.events"),
      to: isAuthenticated ? PORTAL_EVENTS : "/events",
      activeKey: "events",
    })
    if (isDepositsUiEnabled)
      links.push({
        key: "deposit",
        label: t("nav.deposit"),
        to: isAuthenticated ? PORTAL_DEPOSIT : PUBLIC_DEPOSITS,
        activeKey: "deposit",
      })
    if (isAuthenticated && adminAccessResolved && hasAnyAdminAccess)
      links.push({ key: "admin", label: t("nav.admin"), to: PORTAL_ADMIN, activeKey: "admin" })
    if (!isAuthenticated) links.push({ key: "about", label: t("nav.about"), to: "/about", activeKey: "about" })
    return links
  }, [adminAccessResolved, isAuthenticated, hasAnyAdminAccess, t])

  const linkBaseClass = isHomeHero
    ? "text-white hover:text-white drop-shadow-[0_1px_3px_rgba(28,28,46,0.5)]"
    : "text-lib-text hover:text-lib-accent"

  const navShellClass = isAtTop
    ? isHomeHero
      ? "bg-transparent border-b border-white/15"
      : "bg-transparent border-b border-transparent"
    : "bg-lib-card/95 backdrop-blur-md shadow-md shadow-lib-primary/5 border-b border-lib-border"

  const headerHeightClass = scrolled ? "h-14 md:h-16" : "h-16 md:h-20"
  const mobileMenuTopClass = scrolled ? "top-14" : "top-16"

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${navShellClass}`}
      >
        <div className="w-full px-6 md:px-10 lg:px-16">
          <div className={`flex items-center justify-between transition-all duration-500 ${headerHeightClass}`}>
            <div
              className={`flex items-center gap-2 sm:gap-2.5 min-w-0 shrink-0 ${getEntranceClass(shouldAnimateEntrance, "hero-fade-delay-0")}`}
            >
              <Link
                to="/"
                className="flex items-center shrink-0 hover:opacity-80 transition-opacity"
                aria-label="Российская государственная библиотека"
              >
                <img
                  src={publicAssetUrl("images/rgb-logo.svg")}
                  alt="РГБ"
                  className={`h-8 md:h-10 w-auto transition-all duration-500 ${
                    isHomeHero ? "brightness-0 invert drop-shadow-md" : ""
                  }`}
                />
              </Link>
              <span
                className={`shrink-0 select-none text-lg sm:text-xl leading-none font-light ${
                  isHomeHero ? "text-white/45" : "text-lib-text-secondary/50"
                }`}
                aria-hidden
              >
                ×
              </span>
              <NavbarPartners compact={isHome} />
            </div>

            <div
              className={`hidden lg:flex items-center gap-1 ${getEntranceClass(shouldAnimateEntrance, "hero-fade-delay-1")}`}
            >
              {navLinks.map((link) => {
                const active = isNavItemActive(link.activeKey, location.pathname)
                return (
                  <NavAnimatedLink
                    key={link.key}
                    to={link.to}
                    label={link.label}
                    isActive={active}
                    tone={isHomeHero ? "hero" : "solid"}
                  />
                )
              })}
            </div>

            <div
              className={`flex items-center gap-3 ${getEntranceClass(shouldAnimateEntrance, "hero-fade-delay-2")}`}
            >
              {isAuthenticated ? <NotificationBell headerTone={headerTone} /> : null}

              {isAuthenticated ? (
                <div className="relative hidden md:block">
                  <button
                    type="button"
                    onClick={() => setUserMenuOpen((open) => !open)}
                    className={`nav-menu-link flex items-center gap-2 px-3 py-2 transition-colors ${linkBaseClass}`}
                    aria-haspopup="menu"
                    aria-expanded={userMenuOpen}
                  >
                    <span
                      className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-500 ${
                        isHomeHero
                          ? "bg-white/15 border border-white/30 text-white"
                          : "bg-lib-accent/20 text-lib-accent"
                      }`}
                    >
                      <i className="ri-user-line" aria-hidden />
                    </span>
                    <i className={`ri-arrow-down-s-line transition-transform ${userMenuOpen ? "rotate-180" : ""}`} aria-hidden />
                  </button>

                  {userMenuOpen ? (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} aria-hidden />
                      <div className="absolute right-0 top-full mt-2 w-56 bg-lib-card border border-lib-border rounded-xl shadow-lg shadow-lib-primary/10 z-50 overflow-hidden animate-fade-in">
                        <NavbarUserMenuItems
                          layout="dropdown"
                          onNavigate={() => setUserMenuOpen(false)}
                          onLogout={logout}
                        />
                      </div>
                    </>
                  ) : null}
                </div>
              ) : (
                <Link
                  to="/login"
                  className={
                    isHomeHero
                      ? "hidden md:inline-flex items-center gap-2 px-5 py-2.5 border border-white text-white text-sm font-medium rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap"
                      : "hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-lib-accent hover:bg-lib-accent-dark text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                  }
                >
                  <i className="ri-login-box-line" aria-hidden />
                  {t("nav.login")}
                </Link>
              )}

              <button
                ref={menuButtonRef}
                type="button"
                onClick={() => setMobileOpen((open) => !open)}
                className={`lg:hidden w-9 h-9 flex items-center justify-center rounded-md transition-colors ${
                  isHomeHero ? "text-white hover:bg-white/10" : "text-lib-text hover:bg-lib-primary/5"
                }`}
                aria-expanded={mobileOpen}
                aria-controls="mobile-nav-menu"
                aria-label={mobileOpen ? t("nav.closeMenu", "Закрыть меню") : t("nav.openMenu", "Открыть меню")}
              >
                <i className={`text-2xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`} aria-hidden />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div
        className={`fixed inset-0 z-40 lg:hidden transition-all duration-300 ${mobileOpen ? "visible" : "invisible"}`}
      >
        <div
          className={`absolute inset-0 bg-lib-primary/30 backdrop-blur-sm transition-opacity duration-300 ${
            mobileOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMobileOpen(false)}
          aria-hidden
        />
        <div
          id="mobile-nav-menu"
          className={`absolute ${mobileMenuTopClass} left-0 right-0 bg-lib-card border-b border-lib-border transition-all duration-300 ${
            mobileOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4"
          }`}
        >
          <div className="px-6 py-6 flex flex-col gap-1">
            {navLinks.map((link) => {
              const active = isNavItemActive(link.activeKey, location.pathname)
              return (
                <Link
                  key={link.key}
                  to={link.to}
                  className={`nav-menu-link py-3 transition-colors ${
                    active ? "text-lib-accent" : "text-lib-text hover:text-lib-accent"
                  }`}
                >
                  {link.label}
                </Link>
              )
            })}
            <div className="pt-4 mt-2 border-t border-lib-border flex flex-col">
              {isAuthenticated ? (
                <NavbarUserMenuItems
                  layout="mobile"
                  onNavigate={() => setMobileOpen(false)}
                  onLogout={logout}
                />
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-lib-accent hover:bg-lib-accent-dark text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className="ri-login-box-line" aria-hidden />
                  {t("nav.login")}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
